/* The turn grid. One track per tech in today, one slot per turn.
 * Staff headers are drag-reorderable with pointer events (mouse + touch).
 * Squares above a late arrival's start row are slashed: turns they missed.
 *
 * ---- the two axes ----
 *
 * The board is drawn either way round, and which is a salon setting rather
 * than a second board: `ui.boardLayout` is 'columns' out of the box — a column
 * per technician, turns counting down the screen — or 'rows', the same grid
 * transposed, a row per technician with turns counting across.
 *
 * Only two things know about it. The stylesheet turns the flex directions and
 * the sticky edges round (see `.board--rows`), and the geometry in this file
 * asks `alongY()` before it measures anything. Everything else — every square's
 * state, every drag, every menu, the whole of the store — is written once and
 * is the same board either way round, because the markup is the same markup:
 * the technician tracks are still `.col`, and the squares inside one are still
 * in turn order. What changes is the direction they are laid out in.
 *
 * `alongY()` is the one question: does the staff axis run down the screen?
 * False in the classic layout, where technicians run across; true when
 * transposed. Read at call time rather than captured — the setting can change
 * under a mounted board when another station flips it.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

var DRAG_THRESHOLD = 6; // px before a press becomes a drag

export const TurnBoard = {
  name: 'TurnBoard',
  setup: function () {
    var boardEl = Vue.ref(null);
    var drag = Vue.reactive({ active: false, staffId: null, pos: -1 });

    var pending = null; // { staffId, pos, startX, startY, pointerId, target }

    // Which way the staff tracks run. See the note at the top of the file.
    function alongY() { return store.ui.boardLayout === 'rows'; }

    // Which staff slot a pointer lands in. Derived from the track geometry
    // rather than each track's rect: while a swap is animating,
    // getBoundingClientRect() reports mid-flight positions and hit-testing
    // would lag behind the pointer. Tracks are equal-sized with a uniform
    // gap, so the slot pitch is exact — along whichever axis they run.
    function staffIndexAt(evt) {
      if (!boardEl.value) return -1;
      var track = boardEl.value.querySelector('.cols');
      if (!track) return -1;
      var count = track.children.length;
      if (!count) return -1;
      var rect = track.getBoundingClientRect();
      var css = window.getComputedStyle(track);
      var down = alongY();
      var gap = parseFloat(down ? css.rowGap : css.columnGap) || 0;
      var pitch = ((down ? rect.height : rect.width) + gap) / count;
      if (!pitch) return -1;
      var at = down ? evt.clientY - rect.top : evt.clientX - rect.left;
      return Math.max(0, Math.min(count - 1, Math.floor(at / pitch)));
    }

    // Who is drawn in each slot. Hiding whoever is done for today takes their
    // column out, so a slot is a place in this list and not in the roster.
    function drawnStaff() {
      return store.previewColumns.value.map(function (col) { return col.staff; });
    }

    function onPointerDown(evt, staff, pos) {
      if (store.ui.lockOrder) return;
      if (evt.button != null && evt.button !== 0) return;
      pending = {
        staffId: staff.id, pos: pos,
        startX: evt.clientX, startY: evt.clientY,
        pointerId: evt.pointerId, target: evt.currentTarget
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    }

    function onPointerMove(evt) {
      if (!pending) return;
      if (!drag.active) {
        var dx = Math.abs(evt.clientX - pending.startX);
        var dy = Math.abs(evt.clientY - pending.startY);
        // Movement across the staff axis is a reorder; movement along the turn
        // axis is somebody scrolling the day, and is left to the scroll box.
        var along = alongY() ? dy : dx;
        var across = alongY() ? dx : dy;
        if (along < DRAG_THRESHOLD || along < across) return;
        drag.active = true;
        drag.staffId = pending.staffId;
        drag.pos = pending.pos;
        try { pending.target.setPointerCapture(pending.pointerId); } catch (e) { /* noop */ }
      }
      evt.preventDefault();

      // Self-stabilising: after the move the dragged track occupies the slot
      // the pointer is over, so the next sample reads the same index.
      var target = staffIndexAt(evt);
      if (target !== -1 && target !== drag.pos) {
        var drawn = drawnStaff();
        var present = store.presentStaff.value;
        store.movePresentStaff(present.indexOf(drawn[drag.pos]), present.indexOf(drawn[target]));
        drag.pos = target;
      }
    }

    function onPointerUp() {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      if (pending && drag.active) {
        try { pending.target.releasePointerCapture(pending.pointerId); } catch (e) { /* noop */ }
      }
      pending = null;
      drag.active = false;
      drag.staffId = null;
      drag.pos = -1;
    }

    Vue.onBeforeUnmount(onPointerUp);

    /* ---- dragging a square to another technician, or up and down a column ----
     *
     * Any square with work in it, finished or still running, and the landing is
     * a POSITION rather than a column: the pointer picks the technician on one
     * axis and the turn number on the other, the column under it redraws with
     * everything below the landing pushed down, and the drop asks before it
     * writes. The rules for what may land where, and what a landing runs over,
     * are the store's — see js/rules/move.js and `turnMovePlan`.
     *
     * Two things stay here because they only exist while a pointer is down:
     * which square was picked up, and where it is being held.
     */

    var turnDrag = Vue.reactive({
      active: false,
      turnId: null,
      // The column under the pointer and what it would do with the square:
      // 'move' — the preview is already showing it, 'same' — it is being held
      // where it already is, 'blocked', or 'swap' for a square a drop trades
      // places with rather than moves onto.
      overStaffId: null,
      kind: null,
      swapTurnId: null,
      swapKind: null            // 'book' or 'board' — see store.swapTargetFor
    });
    var turnPending = null;
    var holdTimer = null;
    var suppressClick = false;

    /* A finger on a square is far more often the board being scrolled than a
     * square being moved — the grid is a scroll box in both directions and most
     * of it is squares — so on touch the drag is a press that waits. Move
     * before the hold is up and the gesture belongs to the scroll box; hold
     * still and the square lifts under the thumb, which is the moment the
     * scrolling stops being possible and is therefore the moment to show it.
     *
     * A mouse has no such ambiguity and keeps the six pixels it always had. */
    var TOUCH_HOLD_MS = 320;
    var TOUCH_SLOP = 10;

    // The turn a press is about. Over a shared square the two halves are two
    // different services, so the half under the pointer is the one that lifts;
    // anywhere else it is the same turn a click would open — the one still
    // running, else the last in the square.
    function turnUnder(evt, cell) {
      if (!cell.turns.length) return null;
      var el = evt.target && evt.target.closest
        ? evt.target.closest('.frac__part') : null;
      if (el && cell.turns.length > 1) {
        return el.classList.contains('frac__part--b') ? cell.turns[1] : cell.turns[0];
      }
      var live = cell.runningTurnId
        ? cell.turns.find(function (t) { return t.id === cell.runningTurnId; })
        : null;
      return live || cell.turns[cell.turns.length - 1];
    }

    function cellHolds(cell, turnId) {
      return !!turnId && cell.turns.some(function (t) { return t.id === turnId; });
    }

    /* Which turn number the pointer is over, off the gutter rather than off the
     * squares. The gutter is the one part of the grid that does not move while a
     * drag is in flight — the column under the pointer is being redrawn as the
     * pointer crosses it — so hit-testing the squares would mean measuring
     * against the very thing the measurement decides.
     *
     * Its rows ARE the visible ones, in order, which is what makes this correct
     * while finished turns are folded away: row three of the gutter is turn
     * eleven if that is what the gutter says. */
    function rowAt(evt) {
      var el = boardEl.value;
      if (!el) return null;
      var marks = el.querySelectorAll('.gutter__row');
      var list = store.visibleRows.value;
      var n = Math.min(marks.length, list.length);
      if (!n) return null;
      var across = alongY();                     // turns run across the screen
      var at = across ? evt.clientX : evt.clientY;
      for (var i = 0; i < n; i++) {
        var r = marks[i].getBoundingClientRect();
        if (at <= (across ? r.right : r.bottom)) return list[i];
      }
      return list[n - 1];
    }

    // Whether the pointer is still over the grid at all. A little slack, so a
    // drop aimed at the last row does not miss by the two pixels of padding
    // under it.
    var DROP_SLACK = 12;

    function overBoard(evt) {
      var el = boardEl.value;
      if (!el) return false;
      var r = el.getBoundingClientRect();
      return evt.clientX >= r.left - DROP_SLACK && evt.clientX <= r.right + DROP_SLACK &&
        evt.clientY >= r.top - DROP_SLACK && evt.clientY <= r.bottom + DROP_SLACK;
    }

    function onTurnPointerDown(evt, col, cell) {
      if (evt.button != null && evt.button !== 0) return;
      var turn = turnUnder(evt, cell);
      if (!turn) return;                         // an empty square moves nothing
      window.clearTimeout(holdTimer);            // a second finger on the board
      turnPending = {
        turnId: turn.id, staffId: col.staff.id, row: cell.row,
        startX: evt.clientX, startY: evt.clientY,
        pointerId: evt.pointerId, target: evt.currentTarget,
        touch: evt.pointerType === 'touch', held: false
      };
      if (turnPending.touch) {
        holdTimer = window.setTimeout(function () {
          if (!turnPending) return;
          turnPending.held = true;
          liftTurn();
          aimAt(turnPending.staffId, turnPending.row);
        }, TOUCH_HOLD_MS);
      }
      window.addEventListener('pointermove', onTurnPointerMove);
      window.addEventListener('pointerup', onTurnPointerUp);
      window.addEventListener('pointercancel', onTurnPointerUp);
    }

    function liftTurn() {
      turnDrag.active = true;
      turnDrag.turnId = turnPending.turnId;
      // A hold on touch lifts the square before anything has been aimed at, so
      // "picked up" and "moved" are two different states here. Only the second
      // one is a drop; the first, let go of, is still a tap and must still open
      // the square — see the end of onTurnPointerUp.
      turnPending.moved = false;
      try {
        turnPending.target.setPointerCapture(turnPending.pointerId);
      } catch (e) { /* noop */ }
    }

    // Where the square is being held, told to the store so the column under it
    // can redraw. Written only when the pointer crosses into a different square
    // — the preview is a rebuild of a column, and one per pixel would throw the
    // board's own map away sixty times a second.
    function aimAt(staffId, row) {
      var cur = store.ui.turnDrag;
      if (!cur || cur.turnId !== turnDrag.turnId ||
          cur.staffId !== staffId || cur.row !== row) {
        store.ui.turnDrag = { turnId: turnDrag.turnId, staffId: staffId, row: row };
      }
      var plan = store.turnDropPlan.value;
      turnDrag.overStaffId = staffId;
      turnDrag.swapTurnId = null;
      turnDrag.swapKind = null;
      turnDrag.kind = !plan ? null
        : plan.blocked ? 'blocked'
        : plan.noop ? 'same'
        : 'move';
    }

    function aimNowhere() {
      store.ui.turnDrag = null;
      turnDrag.overStaffId = null;
      turnDrag.swapTurnId = null;
      turnDrag.swapKind = null;
      turnDrag.kind = null;
    }

    function onTurnPointerMove(evt) {
      if (!turnPending) return;
      if (!turnDrag.active) {
        var moved = Math.max(Math.abs(evt.clientX - turnPending.startX),
          Math.abs(evt.clientY - turnPending.startY));
        if (turnPending.touch) {
          // Moved before the hold was up: this is a scroll, and the board is
          // left to do what it does with one.
          if (moved > TOUCH_SLOP) { endTurnDrag(); return; }
          return;
        }
        if (moved < DRAG_THRESHOLD) return;
        liftTurn();
      }
      evt.preventDefault();
      turnPending.moved = true;

      // Off the grid entirely is a cancel, not a landing on whichever edge is
      // nearest: `staffIndexAt` clamps to the end columns, so without this a
      // square let go of over the appointments pane would be filed against
      // whoever happens to be drawn at that side of the board.
      if (!overBoard(evt)) { aimNowhere(); return; }

      var idx = staffIndexAt(evt);
      var target = idx === -1 ? null : drawnStaff()[idx] || null;
      var row = rowAt(evt);
      var turn = store.turnOf(turnDrag.turnId);
      if (!target || row == null || !turn) { aimNowhere(); return; }

      /* A square a drop trades places with rather than lands beside — a service
       * held over another service is two clients changing chairs, not a landing
       * — see `swapTargetFor` for which pairs qualify and why.
       *
       * Offered on that square alone rather than over the whole column, so the
       * rest of the column is still somewhere this square can be put down: the
       * gesture is "on top of that one", and aiming a row off it means the
       * ordinary move it looks like. */
      var hit = store.swapTargetFor(turn, target.id);
      if (hit && store.turnRowNumber(hit.mate.id) === row + 1) {
        store.ui.turnDrag = null;
        turnDrag.overStaffId = target.id;
        turnDrag.kind = 'swap';
        turnDrag.swapTurnId = hit.mate.id;
        turnDrag.swapKind = hit.kind;
        return;
      }
      aimAt(target.id, row);
    }

    function onTurnPointerUp() {
      if (turnPending && turnDrag.active && turnPending.moved) {
        suppressClick = true;                      // the drop is not a click
        setTimeout(function () { suppressClick = false; }, 0);
        // Asked of the store, which re-reads the board rather than trusting
        // what the pointer believed on the way past: it refuses, explains and
        // opens the sheet, and a drop that changes nothing does nothing.
        if (turnDrag.kind === 'swap' && turnDrag.swapTurnId) {
          store.openSwapConfirm(turnDrag.turnId, turnDrag.swapTurnId, turnDrag.swapKind);
        } else {
          store.askMoveTurn();
        }
      }
      endTurnDrag();
    }

    function endTurnDrag() {
      window.removeEventListener('pointermove', onTurnPointerMove);
      window.removeEventListener('pointerup', onTurnPointerUp);
      window.removeEventListener('pointercancel', onTurnPointerUp);
      // Every way out of a drag comes through here, including the hold on touch
      // that lifted a square nobody then moved — so the capture is given back
      // here rather than beside the one exit that used to be the only one.
      if (turnPending && turnDrag.active) {
        try {
          turnPending.target.releasePointerCapture(turnPending.pointerId);
        } catch (e) { /* noop */ }
      }
      window.clearTimeout(holdTimer);
      holdTimer = null;
      turnPending = null;
      turnDrag.active = false;
      turnDrag.turnId = null;
      aimNowhere();
    }

    Vue.onBeforeUnmount(endTurnDrag);

    /* ---- cell state ----
     *
     * The stable half of every square — what is in it, whether it is a
     * fraction, whether it clashes with the book — is precomputed by
     * store.boardColumns and arrives as `cell`. Nothing below re-derives it.
     *
     * What is left here is only the part that moves with the pointer: what is
     * armed, and what is being dragged. Each of those bails out immediately
     * when nothing is armed and nothing is in the air, which is the normal
     * state of the board.
     */

    /* Where the square in the air is, and where it would land. Both are read
     * off the cells themselves: the destination column arrives already redrawn
     * (store.previewColumns), with the square in the place the drop would put
     * it and everything below it moved down, so there is nothing for the
     * template to work out — only which of the two copies it is looking at.
     *
     * The lifted one is the square still sitting in the column it came from,
     * dimmed. The ghost is where it is going. On a move within one column they
     * are the same square and it simply travels. */
    function isGhost(cell) { return turnDrag.active && cell.ghost; }

    function isLifted(cell) {
      return turnDrag.active && !cell.ghost && cellHolds(cell, turnDrag.turnId);
    }

    // An occupied square a drag would trade places with. Marked on the square
    // itself rather than on the column, because a swap is about that service
    // and not about that technician's column.
    function isSwapTarget(col, cell) {
      if (!turnDrag.active || !cell.runningTurnId) return false;
      if (cell.runningTurnId === turnDrag.turnId) return false;
      var hit = store.swapTargetFor(store.turnOf(turnDrag.turnId), col.staff.id);
      return !!hit && hit.mate.id === cell.runningTurnId;
    }

    // An open half square. Any armed service completes it — a full turn there
    // simply makes the square worth 1.5.
    function isPairTarget(col, cell) {
      return !!(store.armedService.value && !col.busy && cell.open);
    }

    // What each square would do with the card in the air, asked of the store so
    // that what lights up and what accepts the drop are the same answer. Asked
    // for the body of the square; the top strip is a separate offer, drawn
    // only under the pointer because every square has one.
    function dropKind(col, cell) {
      if (!store.ui.apptDrag) return null;
      var d = store.bookedDropAt(col.staff.id, cell.row, store.ui.apptDrag, false);
      return d ? d.kind : null;
    }

    function isAttachTarget(col, cell) { return dropKind(col, cell) === 'attach'; }

    /* A taken square that a card already over would ask a question of, rather
     * than act on. Hinted like the attach it replaces — same gold, because it is
     * the same "this square will take it" — and the sheet is where the two
     * readings are chosen between.
     *
     * `dropKind` asks with no side of the slash, which on a half square answers
     * for the upper half. That is right for the hint: the whole square is the
     * target either way, and which half a card actually lands on is settled at
     * the drop and shown by `isChooseLive` while the pointer is over it. */
    function isChooseTarget(col, cell) { return dropKind(col, cell) === 'choose'; }

    // Which half of a shared square the card is actually over. The one place the
    // board has to be finer than the square: the two halves are two different
    // turns, and merging into the wrong one is a different square's history.
    function isChooseLive(t) {
      var o = store.ui.apptDrag && store.ui.apptDrag.over;
      return !!(o && o.kind === 'choose' && o.turnId === t.id);
    }

    // One square per column that could take it, so the eye reads which
    // technicians are free rather than every blank square on the board. The
    // drop is looser — anywhere empty in the column — because the turn lands
    // in the rotation's next slot however carefully it was aimed.
    function isApptStartTarget(col, cell) {
      if (cell.row !== col.targetRow) return false;
      return dropKind(col, cell) === 'start';
    }

    /* An open half square that would take the card in its empty side. Hinted
       for the whole square rather than only when the pointer is on that side —
       like the insert strip below, the offer has to be visible from across the
       board or nobody aims at it. Which half the pointer is actually on is
       settled at the drop; see appt-drag's `atOpenHalf`. */
    function isApptPairTarget(col, cell) {
      if (!store.ui.apptDrag || !cell.open || col.busy) return false;
      return store.canStartBooked(col.staff.id, store.ui.apptDrag);
    }

    // A square that would take an insert if the card were held over its top
    // strip. Hinted on every eligible square while dragging, so the move is
    // discoverable rather than something you have to know about.
    function isInsertTarget(col, cell) {
      if (!store.ui.apptDrag) return false;
      if (cell.state !== 'running' && cell.state !== 'done') return false;
      return store.canStartBooked(col.staff.id, store.ui.apptDrag);
    }

    function isOver(kind, col, cell) {
      var o = store.ui.apptDrag && store.ui.apptDrag.over;
      if (!o || o.kind !== kind) return false;
      if (kind === 'attach') return o.turnId === cell.runningTurnId;
      return o.staffId === col.staff.id && (kind === 'start' || o.row === cell.row);
    }

    // Only the empty squares still have anything to work out, and only while
    // something is armed.
    function cellState(col, cell) {
      if (cell.state !== 'empty') return cell.state;
      /* Their day has run out, so every square below the one they are on is
         blocked off rather than only the next one. That is the difference
         between this and every other state here: the rest answer "what would
         happen if you dropped the armed service HERE", and this one answers
         "there is no more of today", which is true of all of them. */
      if (col.offShift) return 'off-shift';
      if (cell.row !== col.targetRow) return 'empty';
      if (!store.ui.armedServiceId) return 'open';
      if (col.busy) return 'blocked';
      return col.staff.serviceIds.indexOf(store.ui.armedServiceId) === -1
        ? 'off-service' : 'targetable';
    }

    // The turn a square is about: the one still running, else the last in it.
    // Both gestures ask this the same way, so they never disagree about which
    // half of a shared square a click landed on.
    function turnOfCell(cell) {
      return cell.runningTurnId
        ? store.turnOf(cell.runningTurnId)
        : cell.turns[cell.turns.length - 1];
    }

    function onCellClick(evt, col, cell) {
      if (suppressClick) return;
      var staff = col.staff;
      if (cell.state === 'missed') {
        store.toast(staff.name + ' missed this turn (joined later).', 'info');
        return;
      }
      if (cell.state === 'skipped') {
        store.toast(staff.name + ' was skipped for this turn. Right-click it to take it off.', 'info');
        return;
      }
      if (isPairTarget(col, cell)) { store.bookTurn(staff.id); return; }
      /* An occupied square opens the same menu right-click does, anchored where
         the click landed. It used to open a modal sheet in the middle of the
         window; the sheet's contents are all in the menu now, and a card under
         the pointer keeps the column the desk was reading in view. */
      if (cell.turns.length) {
        var t = turnOfCell(cell);
        if (t) store.openTurnMenu(t.id, evt ? evt.clientX : 0, evt ? evt.clientY : 0);
        return;
      }
      // Below here every branch puts a square up, so a closed day stops at
      // this line and says why. The squares that already have a turn in them
      // still open, above: reading the day back is not adding to it.
      if (store.refuseWhileClosed()) return;
      /* A blocked-off square with a service already armed. Said rather than
         done, because the armed service is a claim about what goes there and
         this square has been taken out of the rotation for the day.

         Only with something armed, though. With nothing armed the square still
         opens its picker below — that is the deliberate way in, the same one
         the closing warning points at, and somebody who has agreed to stay
         another twenty minutes needs a way to put the square up. */
      if (col.offShift && store.ui.armedServiceId) {
        store.toast(shiftGoneNote(col), 'warn');
        return;
      }
      // Empty square with nothing armed: offer this tech's imminent bookings
      // and their service list, rather than sending the user to the top bar.
      if (!store.ui.armedServiceId) {
        if (col.busy) {
          store.toast(staff.name + ' is still on a service.', 'warn');
          return;
        }
        store.openEmptyPick(staff.id);
        return;
      }
      store.bookTurn(staff.id);
    }

    // What the column says while somebody is out: the reason, not just that
    // they are out. "Done" and "Break" are different questions for the desk.
    function skipBadge(col) {
      var r = store.skipReasonOf(col.staff.id);
      return r ? r.badge : 'Skip';
    }

    function skipTitle(col) {
      var r = store.skipReasonOf(col.staff.id);
      return (r ? r.label : 'Skipping') + ' — turns are going past ' + col.staff.name;
    }

    /* ---- somebody whose day has run out ----
     *
     * Three ways of saying the same fact, at three lengths. The badge is what
     * a narrow column has room for, the tooltip is what the desk hovers for,
     * and the note is what a refused click has to explain — which is the only
     * one that has to say why the squares are blocked rather than merely that
     * they are. All of them name the time, because the time is the thing
     * somebody would want to change.
     */
    function shiftOut(col) {
      var sh = store.shiftOf(col.staff.id);
      return sh && sh.early ? sh.early : null;
    }

    function shiftBadge(col) {
      var out = shiftOut(col);
      return out ? 'To ' + store.formatClock(out) : 'Done';
    }

    function shiftTitle(col) {
      var out = shiftOut(col);
      if (!out) return col.staff.name + ' is finished for the day';
      return col.staff.name + ' finishes at ' + store.formatClock(out) +
        ' — no room left today for anything they do';
    }

    function shiftGoneNote(col) {
      var out = shiftOut(col);
      var svc = store.armedService.value;
      var what = svc ? svc.name : 'That service';
      if (!out) return col.staff.name + ' is finished for the day.';
      return col.staff.name + ' finishes at ' + store.formatClock(out) +
        ', and ' + what + ' would not be over by then. Right-click their name ' +
        'to change the hours, or clear the service and click the square to ' +
        'start it anyway.';
    }

    // The + on a square is a marker, not a list — the names go in its tooltip
    // and, in full, in the menu the square opens.
    function optTitle(t) {
      var names = store.optionsOfTurn(t).map(function (o) { return o.name; });
      if (!names.length) return '';
      return (names.length === 1 ? 'Option: ' : 'Options: ') + names.join(', ') +
        ' — click the square for the detail';
    }

    /* The colour chips on a merged square, in words. Names and their times,
     * because the two questions a merged square raises are what else was done
     * and whether it really was one sitting — and the second is a question about
     * clocks. What the whole square counted for goes last, since that is the
     * number the rotation actually ran on. */
    function mixTitle(t) {
      var list = store.servicesOfTurn(t);
      if (list.length < 2) return '';
      return list.map(function (s) {
        return (s.bookedName || s.name) + ' ' + store.formatClock(s.startedAt) +
          '–' + (s.endedAt ? store.formatClock(s.endedAt) : 'now');
      }).join(' · ') + ' — one sitting, ' + store.pluralTurns(t.turnWeight) +
        '. Click the square for the detail.';
    }

    // Right-click a name to bypass that tech. The press that opens it must not
    // also start a column drag.
    function onHeadContext(evt, staff) {
      evt.preventDefault();
      onPointerUp();
      store.openStaffMenu(staff.id, evt.clientX, evt.clientY);
    }

    /* Right-click a square: everything it can be told, at the pointer.
     *
     * No longer conditional on a plugin. It used to be — with requests off the
     * press jumped straight to the appointment picker, and with no book either
     * it was left alone entirely — because back then the menu had nothing of
     * its own to say and one holding a single item would have been a step
     * backwards. Now it carries what the sheet used to, which is worth opening
     * whatever is switched on.
     *
     * An empty square still has nothing, so the browser's own menu comes up
     * there, as anywhere else on the page. */
    function onCellContext(evt, col, cell) {
      // A red square: take it off, from a sheet that says why it was charged.
      if (cell.state === 'skipped' && cell.skipOrdinal != null) {
        evt.preventDefault();
        store.openSkipRemove(col.staff.id, cell.skipOrdinal);
        return;
      }
      if (!cell.turns.length) return;
      var turn = turnOfCell(cell);
      if (!turn) return;
      evt.preventDefault();
      store.openTurnMenu(turn.id, evt.clientX, evt.clientY);
    }

    /* ---- the keyboard ----
     * A square does what a click does on Enter or Space, and what a right-click
     * does on the menu key or Shift+F10. The menus anchor at a point, and a key
     * has none, so they open over the middle of the square that has focus.
     *
     * Only the squares worth pressing are tab stops — anything with a turn in
     * it, the next open square in each column, a missed or skipped one — so Tab
     * walks the live part of the day instead of every blank under it. The
     * arrows reach the rest, moving a square at a time in whichever direction
     * the board is drawn. */
    function centreOf(el) {
      var r = el.getBoundingClientRect();
      return { clientX: r.left + r.width / 2, clientY: r.top + r.height / 2 };
    }

    function cellTab(col, cell) {
      if (cell.turns.length || cell.row === col.targetRow) return 0;
      return cell.state === 'missed' || cell.state === 'skipped' ? 0 : -1;
    }

    function cellLabel(col, cell) {
      var where = col.staff.name + ', turn ' + (cell.row + 1) + ': ';   // rows count from 0, the gutter from 1
      if (cell.turns.length) {
        return where + cell.turns.map(function (t) {
          return turnTitle(t) + (t.endedAt == null ? ', running' : ', finished');
        }).join('; ');
      }
      var state = cellState(col, cell);
      var says = {
        missed: 'missed', skipped: 'skipped', 'off-shift': 'after their hours',
        open: 'next open square', targetable: 'next open square, takes the armed service',
        'off-service': 'next open square, does not do the armed service', blocked: 'next open square, still on a service'
      };
      return where + (says[state] || 'empty');
    }

    // The square one `step` (+1 or -1) away: down the same column when `along`,
    // otherwise the same row of the next column over. Null at an edge.
    function neighbour(el, along, step) {
      var colEl = el.closest('.col');
      if (!colEl) return null;
      if (along) {
        var cells = Array.prototype.slice.call(colEl.querySelectorAll('.cell'));
        return cells[cells.indexOf(el) + step] || null;
      }
      var cols = Array.prototype.slice.call(colEl.parentNode.children);
      var next = cols[cols.indexOf(colEl) + step];
      return next ? next.querySelector('.cell[data-row="' + el.dataset.row + '"]') : null;
    }

    function onCellKey(evt, col, cell) {
      var el = evt.currentTarget;
      if (evt.key === 'Enter' || evt.key === ' ') {
        evt.preventDefault();
        onCellClick(centreOf(el), col, cell);
        return;
      }
      if (evt.key === 'ContextMenu' || (evt.key === 'F10' && evt.shiftKey)) {
        var at = centreOf(el);
        evt.preventDefault();
        onCellContext({ clientX: at.clientX, clientY: at.clientY, preventDefault: function () {} }, col, cell);
        return;
      }
      // Columns-up, turns run down the screen; turned a quarter, across it.
      var down = !alongY();
      var moves = {
        ArrowDown: [down, 1], ArrowUp: [down, -1],
        ArrowRight: [!down, 1], ArrowLeft: [!down, -1]
      };
      var m = moves[evt.key];
      if (!m) return;
      var to = neighbour(el, m[0], m[1]);
      if (!to) return;
      evt.preventDefault();
      to.focus();
    }

    // A name opens its menu from the keyboard too: bypass, hours, and the rest.
    function onHeadKey(evt, staff) {
      if (evt.target !== evt.currentTarget) return;   // the next-booking clock inside it
      if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'ContextMenu' &&
          !(evt.key === 'F10' && evt.shiftKey)) return;
      evt.preventDefault();
      var at = centreOf(evt.currentTarget);
      store.openStaffMenu(staff.id, at.clientX, at.clientY);
    }

    function turnTitle(t) {
      var bits = [t.serviceName];
      if (t.clientName) bits.push(t.clientName);
      if (t.sourceServiceName) bits.push(t.sourceServiceName);
      if (isUnlinked(t)) bits.push('no appointment');
      return bits.join(' · ');
    }

    // A turn running with nothing from the book behind it: either a walk-in, or
    // a booking nobody has attached yet. Only worth saying when there is a book
    // to compare against, so it follows the scheduling plugin.
    function isUnlinked(t) {
      return !!(store.schedulingOn.value && t.endedAt == null && !t.appointmentServiceId);
    }

    // The board and the book disagree about who did this one. A map lookup
    // now — the square's own flag is precomputed as `cell.mismatch`.
    function mismatch(t) { return store.turnStaffMismatch(t); }

    // The tech label ends in its own full stop ("Thu Q."), so it goes in the
    // middle of a clause rather than at the end of one.
    function mismatchTitle(t) {
      var m = mismatch(t);
      if (!m) return '';
      var name = store.schedulingName.value;
      // Once the client has paid it is the sale that disagrees, and the sale
      // is what the commission follows, so that is what this names.
      if (m.onSale) {
        return 'Not who the sale has. ' + name + ' rang up ' + m.clientName + '’s ' +
          m.serviceName + ' under ' + m.inBook + '; the board has ' + m.onBoard +
          '. Move the square if the board is wrong, or fix the sale — the commission follows the sale.';
      }
      return 'Wrong technician in ' + name + '. The booking has ' + m.inBook +
        ' against ' + m.clientName + '’s ' + m.serviceName + '; the board has ' +
        m.onBoard + '. Fix it in ' + name + ' so the ticket goes to the right person.';
    }

    var unlinkedTitle = 'Running with no appointment attached — a walk-in, or ' +
      'right-click the square to link one.';

    function elapsed(turn) {
      // Held until the client is checked in: no clock to show yet.
      if (turn.waiting && turn.endedAt == null) return 'Waiting';
      var end = turn.endedAt == null ? store.now.value : turn.endedAt;
      return store.formatDuration(end - turn.startedAt);
    }

    /* ---- how long is left on a booked service ----
     *
     * Counted from when the turn actually started, not from the clock time the
     * book had it finishing: a client twenty minutes late would otherwise show
     * as overrunning the moment they sat down, which is the salon's problem
     * but not this technician's.
     */
    var SOON_MS = 5 * 60000;

    function remainingMs(turn) {
      if (!turn.scheduledMinutes || turn.waiting) return null;
      var end = turn.endedAt == null ? store.now.value : turn.endedAt;
      return turn.scheduledMinutes * 60000 - (end - turn.startedAt);
    }

    /* ---- how far a finished turn ran from what it was booked for ----
     *
     * The countdown disappears the moment a turn stops, and with it the only
     * record on the board of whether the service ran to time. That is the one
     * thing about a finished square worth carrying: the desk is about to book
     * the rest of this technician's afternoon against lengths the book claims,
     * and a pedicure that reliably runs a quarter of an hour long is the
     * reason the four o'clock is late.
     *
     * Five minutes either way is on time. Anything inside that is the ordinary
     * slack of starting and stopping a square by hand, and marking it would put
     * a number on almost every finished turn on the board — which is how a
     * mark stops meaning anything.
     */
    var DRIFT_MS = 5 * 60000;

    function driftText(turn) {
      if (turn.endedAt == null || !turn.scheduledMinutes) return '';
      var over = (turn.endedAt - turn.startedAt) - turn.scheduledMinutes * 60000;
      if (Math.abs(over) <= DRIFT_MS) return '';
      return (over > 0 ? '+' : '-') + store.formatMins(Math.abs(over));
    }

    // A countdown is only a countdown while the service is running. A finished
    // square reads as the length it took — "5m left" on work that is over says
    // nothing, and it is exactly what a square linked to its booking after the
    // fact would otherwise show — plus how far off the booked length that was.
    function timerText(turn) {
      if (turn.endedAt != null) {
        var drift = driftText(turn);
        return drift ? elapsed(turn) + ' · ' + drift : elapsed(turn);
      }
      var left = remainingMs(turn);
      if (left == null) return elapsed(turn);
      return elapsed(turn) + (left < 0
        ? ' · ' + store.formatMins(-left) + ' over'
        : ' · ' + store.formatMins(left) + ' left');
    }

    // Half a square has no room for the running countdown, so it carries the
    // colour alone and the tooltip says the rest. The finished difference is
    // short enough to fit, and it is the half of that pair worth reading: a
    // square nobody is watching any more is exactly where the number would
    // otherwise be lost. It does cost the line a size — see `has-drift` — on
    // the narrowest column the board allows, where the untrimmed string would
    // reach across the slash into the other half's name.
    function fracTimerText(turn) {
      var drift = driftText(turn);
      return drift ? elapsed(turn) + ' ' + drift : elapsed(turn);
    }

    // Amber inside the last five minutes, red once it is over. Live turns only:
    // a finished square that ran a few minutes long is history, and colouring
    // every one of them red would make the state mean nothing.
    function timerState(turn) {
      if (turn.endedAt != null) return null;
      var left = remainingMs(turn);
      if (left == null) return null;
      if (left < 0) return 'is-over';
      return left <= SOON_MS ? 'is-soon' : null;
    }

    /* ---- how far through a running square is: the filling bar ----
     *
     * The salon's second way of drawing a live square — `ui.turnProgress` is
     * 'light' out of the box, the blinking gold light this board has always
     * had, or 'bar', which fills the same timer strip left to right as the
     * booked length runs down. Neither is a second board: the strip, its
     * colours and its wording are the same either way, and this decides only
     * what happens inside it.
     *
     * How far through, 0 to 100, or null for a strip that keeps its light.
     * Three things say null:
     *
     *   the salon has not asked for the bar;
     *   the turn is over — a finished square is not making progress, and its
     *     strip has stopped being a countdown and become how long it took;
     *   the turn has no booked and no usual length, so there is nothing for
     *     the elapsed time to be a fraction OF.
     *
     * That last one is why the light is still here rather than replaced. A
     * walk-in on a service nobody has given a length to would otherwise be a
     * square that is running and looks empty — the one thing the strip has to
     * say, unsaid, on exactly the squares nobody is expecting.
     *
     * Full at the booked length rather than running on past it: over its time
     * the strip is already red and the number on it says by how much, and a bar
     * that kept going would have to grow a second meaning to show it.
     */
    function turnPct(turn) {
      if (store.ui.turnProgress !== 'bar' || turn.endedAt != null) return null;
      var left = remainingMs(turn);
      if (left == null) return null;
      var whole = turn.scheduledMinutes * 60000;
      // Two decimals: the strip is redrawn once a second and the width eases
      // between those, so a whole percent would stand still for half a minute
      // on a long service and then jump.
      return Math.round(Math.max(0, Math.min(1, 1 - left / whole)) * 10000) / 100;
    }

    /* ---- a half square with one of its two services still running ----
     *
     * Two halves share one square and each carries its own small countdown,
     * which is the one place the bar could not follow: a pill five characters
     * wide has nowhere to fill. So a half square with something running on it
     * borrows the whole square's strip instead — the full width along the
     * bottom, the running half's countdown in it, filling as that service runs
     * down, exactly as it would if the square were a whole turn.
     *
     * Which leaves the question the strip creates: a bar across the bottom of a
     * square holding two services says nothing about WHICH of them it is
     * counting. So the other half goes to greyscale. Between them the square
     * reads at a glance the way a whole one does — the live work in colour with
     * its own bar underneath, the half that is over still legible, still
     * there, and plainly not what the bar is about.
     *
     * The running half, or null on everything else — a finished pair, a square
     * with no service on it, and the whole board while the salon is on the
     * blinking light, which is the setting that says leave the squares alone.
     */
    function fracLead(cell) {
      if (!cell.half || !cell.runningTurnId) return null;
      if (store.ui.turnProgress !== 'bar') return null;
      return cell.turns.find(function (t) {
        return t.id === cell.runningTurnId;
      }) || null;
    }

    // The half the bar is not about. An open half has no turn to grey — it is
    // already empty — so this only ever finds a service that has finished.
    function fracDim(cell, turn) {
      var lead = fracLead(cell);
      return !!lead && turn.id !== lead.id;
    }

    /* ---- a start away from the booking: the head of the bar ----
     *
     * A booked square that went up more than the salon's margin before or after
     * its booking (js/rules/start-offset.js) has that stretch drawn at the
     * left of the bar — green for early, red for late — as wide as the offset
     * is against the booked length. Bar only, and running squares only, for the
     * same reasons as `turnPct`: the light has no length to take a share of,
     * and a finished strip is no longer a bar. The tooltip and the turn menu
     * say it in words on every square.
     *
     * Read against the book as it is now, so a booking moved to when the
     * client really sat down takes the mark away on the next redraw.
     */
    function offsetMark(turn) {
      if (!turn || turnPct(turn) == null) return null;
      var o = store.startOffsetOf(turn);
      if (!o || !o.flag) return null;
      var pct = store.offsetPct(o.flag.ms, turn.scheduledMinutes);
      return pct ? { kind: o.flag.kind, pct: pct } : null;
    }

    // "Started 7m late — booked for 10:00", or '' inside the margin.
    function offsetTitle(turn) {
      var o = store.startOffsetOf(turn);
      if (!o || !o.flag) return '';
      return 'Started ' + store.formatMins(o.flag.ms) + ' ' +
        (o.flag.kind === 'late' ? 'behind' : 'ahead of') +
        ' the booking — booked for ' + store.formatClock(o.bookedAt) +
        ', started ' + store.formatClock(turn.startedAt) + '.';
    }

    function timerTitle(turn) {
      if (!turn) return '';
      return [timerLengthTitle(turn), offsetTitle(turn)].filter(Boolean).join('\n');
    }

    // Where the countdown came from matters to whoever is reading it: a
    // booking is what this client is down for, a type default is only what
    // this kind of work usually takes.
    function timerLengthTitle(turn) {
      if (!turn.scheduledMinutes) return '';
      // Past tense once it is over, and the difference spelled out: "+13m" on
      // the square is a glance, and this is where it is read properly.
      if (turn.endedAt != null) {
        var over = (turn.endedAt - turn.startedAt) - turn.scheduledMinutes * 60000;
        var what = (turn.appointmentServiceId || turn.sourceServiceName)
          ? (turn.sourceServiceName || 'The booking') + ' was booked for '
          : turn.serviceName + ' usually takes ';
        var by = store.formatMins(Math.abs(over));
        return what + turn.scheduledMinutes + ' minutes — it ran ' +
          (Math.abs(over) <= DRIFT_MS
            ? 'to time'
            : by + (over > 0 ? ' long' : ' short'));
      }
      var due = turn.startedAt + turn.scheduledMinutes * 60000;
      var at = ' — due to finish at ' + store.formatClock(due);
      if (turn.appointmentServiceId || turn.sourceServiceName) {
        return (turn.sourceServiceName || 'The booking') + ' is booked for ' +
          turn.scheduledMinutes + ' minutes' + at;
      }
      return turn.serviceName + ' usually takes ' + turn.scheduledMinutes +
        ' minutes' + at + '. Set under Settings → Services.';
    }

    /* ---- header clocks: how long free, how long until the next booking ---- */

    // Time on their hands since the last service ended — or since the day's
    // first service began, for anyone who has not had one yet. Meaningless
    // while they are in service, where the square's own timer is the number
    // that matters, and there is no such thing before the floor starts.
    // Minute clock again: formatMins rounds to whole minutes, so this is the
    // same string sixty times a second otherwise. With nothing running on the
    // board it is also the last second-by-second dependency in here, so a
    // quiet board now stops re-rendering between minutes altogether.
    // What stopped this column's idle clock, in words, or nothing while it is
    // still running. Whichever came first: a technician who went on a break at
    // four is not still accruing idle because the book was shut at six.
    function idleStopNote(col) {
      var closedAt = store.dayClosedAt.value;
      var spans = store.skipSpansFor(col.staff.id);
      var last = spans.length ? spans[spans.length - 1] : null;
      var outAt = last && last.to == null ? last.from : null;
      if (outAt != null && (closedAt == null || outAt <= closedAt)) {
        return col.staff.name + ' went out of the rotation at ' +
          store.formatClock(outAt);
      }
      if (closedAt != null) return 'the book was shut at ' + store.formatClock(closedAt);
      return '';
    }

    // `idleUntil` rather than the clock: the day being shut, or this tech being
    // out of the rotation, stops the count where it stopped rather than
    // running it on behind them. A frozen number still says something — it is
    // what they had built up at the moment they stepped out.
    function idleLabel(col) {
      if (col.busy) return null;
      var since = store.idleSince(col.staff.id);
      if (since == null) return '—';
      return store.formatMins(Math.max(0, store.idleUntil(col.staff.id) - since));
    }

    function idleTitle(col) {
      if (col.busy) return col.staff.name + ' is in service';
      var since = store.idleSince(col.staff.id);
      if (since == null) {
        return 'Nothing has started yet today — the idle clock starts with the ' +
          'day’s first service';
      }
      // Why the number is not moving. The badge on the column says they are
      // out; this says what that did to the clock beside it. Asked of the two
      // conditions rather than of the frozen timestamp, which is a minute
      // behind the wall clock and would leave the tooltip lagging the badge.
      var stopped = idleStopNote(col);
      if (stopped) {
        return 'Idle stopped — ' + stopped + '. Free since ' + store.formatClock(since);
      }
      if (store.turnsFor(col.staff.id).length) {
        return 'Free since ' + store.formatClock(since);
      }
      // Marked in before the floor got going: the clock runs from the day, not
      // from them, so the tooltip says which it is showing.
      var marked = store.availableSince(col.staff.id);
      return marked < since
        ? 'Marked in at ' + store.formatClock(marked) + ' — idle counted from the ' +
          'day’s first service at ' + store.formatClock(since)
        : 'Marked in at ' + store.formatClock(marked);
    }

    // Minutes until their next booking starts; negative once it is overdue.
    // Read off the minute clock: the label is in whole minutes, so a second
    // hand could only ever produce the same number again.
    function nextMins(col) {
      var b = store.nextBookingFor(col.staff.id);
      if (!b) return null;
      return Math.round((b.svc.startsAt - store.minuteNow.value) / 60000);
    }

    function nextLabel(col) {
      var m = nextMins(col);
      if (m == null) return '—';
      return m <= 0 ? 'due' : store.formatMins(m * 60000);
    }

    function nextTitle(col) {
      var b = store.nextBookingFor(col.staff.id);
      if (!b) {
        return 'Nothing coming that needs ' + col.staff.name + ' specifically';
      }
      return 'Next: ' + store.formatClock(b.svc.startsAt) + ' — ' +
        b.svc.name + ' (' + b.appt.clientName + ')' +
        (b.why === 'only'
          ? ' — only ' + col.staff.name + ' can take it'
          : ' — booked with ' + col.staff.name);
    }

    // Amber inside ten minutes, red once the client is due: both mean "do not
    // hand this tech a walk-in".
    function nextClass(col) {
      var m = nextMins(col);
      if (m == null) return null;
      if (m <= 0) return 'col__clock-item--due';
      return m <= 10 ? 'col__clock-item--soon' : null;
    }

    /* ---- what the next booking is: the letter beside the clock ----
     *
     * The countdown says when; this says what, in the same letter the square
     * will carry when it goes up, in the same colour. Starred when the book
     * names them, which is this board's reading of a client request — and a
     * request is the difference between "she is busy at four" and "somebody
     * is busy at four", so it is worth a glyph rather than a tooltip.
     *
     * "?" for a booking whose service has never been mapped, matching the Due
     * now chips: the board does not know what it is, and saying so is better
     * than a blank that reads as nothing coming.
     */
    function nextChip(col) {
      var b = store.nextBookingFor(col.staff.id);
      if (!b) return null;
      return {
        letter: b.mapped ? b.mapped.abbrev : '?',
        color: b.mapped ? b.mapped.color : store.defaultServiceColor,
        requested: b.why === 'requested'
      };
    }

    /* ---- the hover card on the next-booking clock ----
     *
     * A `title` could carry the appointment, but not the list of who else
     * could take it, and that list is the half of this worth reading. So it
     * is a real card — one at a time, positioned against whatever the pointer
     * is over, and fixed rather than absolute because the board is a scroll
     * box in both directions and would clip anything inside it.
     *
     * It stays open while the pointer is on the card itself, so a long cover
     * list can be read down; the short delay on the way out is what makes the
     * gap between the clock and the card crossable.
     */
    var peek = Vue.reactive({ staffId: null, x: 0, y: 0, maxH: 0 });
    var peekTimer = null;
    var PEEK_W = 264;

    // Computed rather than called from the template: the card reads it a
    // dozen times over, and the cover list is a pass over the roster.
    var peekDetail = Vue.computed(function () {
      return peek.staffId ? store.nextBookingDetail(peek.staffId) : null;
    });

    var peekStaffName = Vue.computed(function () {
      var s = peek.staffId ? store.staffOf(peek.staffId) : null;
      return s ? s.name : '';
    });

    function openPeek(evt, col) {
      window.clearTimeout(peekTimer);
      if (!store.nextBookingFor(col.staff.id)) { peek.staffId = null; return; }
      var r = evt.currentTarget.getBoundingClientRect();
      // Centred on the clock, then pulled back inside the window: a column at
      // either edge of the board is often at the edge of the screen too.
      var half = PEEK_W / 2;
      peek.x = Math.min(Math.max(r.left + r.width / 2, half + 8),
        window.innerWidth - half - 8);
      peek.y = r.bottom + 6;
      peek.maxH = Math.max(120, windowHeight() - peek.y - 12);
      peek.staffId = col.staff.id;
    }

    function holdPeek() { window.clearTimeout(peekTimer); }

    function closePeek() {
      window.clearTimeout(peekTimer);
      peekTimer = window.setTimeout(function () { peek.staffId = null; }, 140);
    }

    Vue.onBeforeUnmount(function () { window.clearTimeout(peekTimer); });

    // The card's own wording. Each of these is a whole sentence rather than a
    // template stitching text around {{ }}, because the compiler eats the
    // whitespace either side of an interpolation and the seams show.
    function peekWhen(d) {
      var m = Math.round((d.startsAt - store.minuteNow.value) / 60000);
      var at = store.formatClock(d.startsAt);
      if (m <= 0) return at + ' — due now';
      return at + ' — in ' + store.formatMins(m * 60000);
    }

    function peekLength(d) {
      if (!d.minutes) return '';
      return d.minutes + ' min, to ' + store.formatClock(d.endsAt);
    }

    function peekLock(d, name) {
      var who = name || 'this column';
      // Nothing to say about a request. The star at the top of the card
      // already says the client asked for this technician, and the column the
      // card was opened from says which one — a sentence repeating both, and
      // then explaining what the board does about it, was three lines to read
      // past on a card that is read at a glance. The board used to apologise
      // here as well, for not being able to tell whether anyone had been asked
      // for at all; the star answers that where it takes the answer.
      // "In the rotation" rather than "in today": a colleague being skipped
      // still does the service, and may well be back before this one runs.
      // They show up in the list below, so the sentence must leave room.
      if (d.why === 'only') {
        return who + ' is the only one in the rotation who does it.';
      }
      return '';
    }

    /* ---- the star on the card ----
     *
     * The one thing the booking system cannot tell the board is whether the
     * client asked for this technician by name, so this is where it is told.
     * The card is already open in front of somebody looking at that booking,
     * which makes it the cheapest moment to answer.
     *
     * A booking nobody is named on cannot be a request for anybody — marking
     * it would record a request for no one — so there is no star on those,
     * the same rule the right-click menu goes by.
     */
    var peekTech = Vue.computed(function () {
      var d = peekDetail.value;
      return d ? store.bookedStaffFor(d.svc) : null;
    });

    // Only where the desk may mark one: with a booking system connected the
    // star just says what the book has (`store.deskMarksRequests`).
    var peekCanMark = Vue.computed(function () {
      return !!(store.deskMarksRequests.value && peekDetail.value && peekTech.value);
    });

    var peekStarTitle = Vue.computed(function () {
      var d = peekDetail.value;
      if (!d || !peekTech.value) return '';
      return d.requested
        ? 'A client request for ' + peekTech.value.name + ' — click to unmark it'
        : 'Click if the client asked for ' + peekTech.value.name + ' by name';
    });

    function togglePeekRequest() {
      var d = peekDetail.value;
      if (!d || !peekCanMark.value) return;
      store.toggleApptRequest(d.appt, d.svc);
    }

    // Why somebody cannot take it, in the words the floor uses. A name in the
    // book pins them either way; only what it is called changes with the
    // requests switch, because only that part was ever an inference.
    function coverNote(c) {
      if (c.ok) return 'free';
      if (c.why === 'pinned') {
        return (store.ui.requestsOn ? 'requested at ' : 'booked at ') +
          store.formatClock(c.at);
      }
      if (c.why === 'busy') {
        return c.until ? 'in service to ' + store.formatClock(c.until) : 'in service';
      }
      if (c.why === 'skipped') return 'being skipped';
      if (c.why === 'shift') return 'gone by ' + store.formatClock(c.until);
      if (c.why === 'break') return (c.label || 'break') + ' at ' + store.formatClock(c.at);
      return '';
    }

    // Empty when there is nothing to add: with nobody else on the floor able
    // to do it, the line above has already said so and saying it twice makes
    // the card read as though it had found something.
    function coverLine(d) {
      var free = d.cover.filter(function (c) { return c.ok; }).length;
      if (!d.cover.length) return '';
      if (!free) return 'Nobody else is free while it runs:';
      return free + (free === 1 ? ' other tech could' : ' other techs could') +
        ' take it:';
    }

    /* ---- fitting the board to the window ----
     *
     * The grid is its own scroll box in both directions: staff headers and the
     * turn gutter stay put (sticky) while the squares move under them. The box
     * is sized from the window rather than from a turn count, so the board
     * fills the screen it is on and grows past it as the day fills up.
     *
     * Which axis the day grows along is the layout setting, so every measure
     * below asks `alongY()` rather than naming a direction. The box's height is
     * capped from the window either way — that is the screen, not the grid —
     * but what that height buys changes: rows of turns in the classic layout,
     * and simply the technicians who fit when transposed.
     */

    var edge = Vue.reactive({ left: false, right: false, overflows: false });
    var stuckToEnd = true;
    var BOTTOM_GAP = 22;                       // breathing room under the grid

    // The turn axis, measured off the gutter: its corner — the one square that
    // is not a turn — and one turn slot, along whichever direction the turns
    // run. In the transposed layout that corner is as wide as a staff header,
    // which is what the numbers along the top are offset by.
    function turnMetrics() {
      var el = boardEl.value;
      if (!el) return null;
      var gutter = el.querySelector('.gutter');
      var head = el.querySelector('.gutter__head');
      var slot = el.querySelector('.gutter__row');
      if (!gutter || !head || !slot) return null;
      var css = window.getComputedStyle(gutter);
      var across = alongY();                   // turns run across the screen
      var headBox = head.getBoundingClientRect();
      var slotBox = slot.getBoundingClientRect();
      return {
        head: across ? headBox.width : headBox.height,
        slot: across ? slotBox.width : slotBox.height,
        gap: parseFloat(across ? css.columnGap : css.rowGap) || 0
      };
    }

    /* How much window there actually is under the top of the board.
     *
     * `innerHeight` on a phone is the window with its browser toolbars counted
     * in, and those collapse as the page scrolls. Sized against it the board is
     * a toolbar taller than the screen — the last row sits under the address
     * bar, which is where the arrow that scrolls the columns lives. The visual
     * viewport is what is actually visible, which is the question being asked.
     *
     * Falls back to `innerHeight` for anything that does not have it, which is
     * every browser this board ran in before phones were a target. */
    function windowHeight() {
      var vv = window.visualViewport;
      return vv ? vv.height : window.innerHeight;
    }

    /* What is parked over the bottom of the window. On a phone that is the
     * fixed nav bar, and the armed strip above it when a service is up — both
     * of which the board would otherwise be sized straight through, putting its
     * last row and the arrows that scroll it underneath the nav.
     *
     * Measured rather than declared: the strip comes and goes, and the bar is a
     * safe-area inset taller on some phones than on others.
     *
     * The two by name rather than the dock that holds them, because the dock
     * also holds the ⋯ menu — which is a quarter of the screen tall, opens over
     * a scrim, and is gone again in a second. Sized against that, opening the
     * menu shrank the board to a third of itself and closing it grew it back,
     * so a glance at Settings reshuffled the day behind it. */
    function bottomChrome() {
      var h = 0;
      ['.pbar', '.parmed'].forEach(function (sel) {
        var el = document.querySelector(sel);
        if (el) h += el.getBoundingClientRect().height;
      });
      return h;
    }

    function fitToWindow() {
      var el = boardEl.value;
      if (!el) return;
      var top = el.getBoundingClientRect().top;
      var avail = Math.max(180, windowHeight() - top - BOTTOM_GAP - bottomChrome());
      el.style.maxHeight = avail + 'px';

      var m = turnMetrics();
      if (!m || !m.slot) return;
      // How much box there is along the turn axis. Across the screen that is
      // the board's own inner width, which already has the vertical scrollbar
      // taken out of it. Down the screen it is the height just set, less the
      // horizontal scrollbar — otherwise the bottom row is left half-cut.
      var box = alongY()
        ? el.clientWidth
        : avail - (el.offsetHeight - el.clientHeight);
      // Whole turns only: a half-visible square at the far edge reads as a bug.
      store.setViewportRows(Math.floor((box - m.head - m.gap) / (m.slot + m.gap)));
    }

    // The far end of the grid is the leading edge of the day — the highest turn
    // number anyone has reached — so that is where the board sits by default,
    // unless somebody has scrolled back to look at earlier turns.
    function scrollToEnd() {
      var el = boardEl.value;
      if (!el) return;
      if (alongY()) el.scrollLeft = el.scrollWidth;
      else el.scrollTop = el.scrollHeight;
    }

    // ...but the *newest* turn is not necessarily down there. A turn lands in
    // its tech's next open row, so a booking for whoever is furthest behind
    // appears near the start. When one is added, follow the turn rather than
    // the end — being able to see the square that just appeared is the point.

    // The near edge of the scrollable area proper, along the turn axis: the
    // gutter's corner is pinned over it, so anything scrolled flush to the box
    // would sit underneath it. Its height in the classic layout, its width in
    // the transposed one, where it is as wide as the staff headers beneath it.
    function viewStart(el) {
      var head = el.querySelector('.gutter__head');
      var box = el.getBoundingClientRect();
      var r = head ? head.getBoundingClientRect() : null;
      return alongY()
        ? box.left + (r ? r.width : 0)
        : box.top + (r ? r.height : 0);
    }

    // Where the board's own scroll box ends, along the turn axis.
    function viewEnd(el) {
      var box = el.getBoundingClientRect();
      return alongY() ? box.right : box.bottom;
    }

    // A scroll along the turn axis, whichever one that is.
    function scrollAlong(el, delta) {
      el.scrollBy(alongY()
        ? { left: delta, behavior: 'smooth' }
        : { top: delta, behavior: 'smooth' });
    }

    function revealTurn(turnId) {
      var el = boardEl.value;
      if (!el) return false;
      var cell = el.querySelector('[data-turn-id="' + turnId + '"]');
      if (!cell) return false;
      var r = cell.getBoundingClientRect();
      var near = alongY() ? r.left : r.top;
      var far = alongY() ? r.right : r.bottom;
      var start = viewStart(el);
      var end = viewEnd(el);
      if (near >= start - 1 && far <= end + 1) return true;
      scrollAlong(el, near < start ? near - start : far - end);
      return true;
    }

    // Arming a service lights up every square the turn could land in — each
    // eligible tech's next open turn, at whatever depth that is, plus the blue
    // suggestion on whoever is up. Those are rarely level with each other, so a
    // board scrolled to the newest turn can leave the whole set out of sight.
    // Bring the span back into view.
    function revealEligible() {
      var el = boardEl.value;
      if (!el || !store.ui.armedServiceId) return;
      var marks = el.querySelectorAll('.cell--targetable, .cell--pair, .cell--suggested');
      if (!marks.length) return;

      var across = alongY();
      var near = Infinity;
      var far = -Infinity;
      marks.forEach(function (m) {
        var r = m.getBoundingClientRect();
        var a = across ? r.left : r.top;
        var b = across ? r.right : r.bottom;
        if (a < near) near = a;
        if (b > far) far = b;
      });

      var start = viewStart(el);
      var end = viewEnd(el);
      if (near >= start - 1 && far <= end + 1) return;      // all already in view

      // Too long to fit them all: show the span from its near end, which is
      // where the queue starts — the deepest spots belong to whoever is
      // furthest ahead.
      scrollAlong(el, (far - near > end - start || near < start)
        ? near - start
        : far - end);
    }

    function newestTurn() {
      var newest = null;
      store.state.turns.forEach(function (t) {
        if (!newest || t.startedAt > newest.startedAt) newest = t;
      });
      return newest;
    }

    /* The fades and the two arrows are the horizontal edges of the scroll box,
     * and they stay horizontal whichever way round the board is — what changes
     * is what is off the side of it. Classic layout: more technicians. When
     * transposed: earlier and later turns, and the technicians who do not fit
     * are scrolled to vertically, which needs no arrows because a list that
     * scrolls down is the one thing every screen already does. */
    function onScroll() {
      var el = boardEl.value;
      if (!el) return;
      edge.overflows = el.scrollWidth > el.clientWidth + 1;
      edge.left = el.scrollLeft > 2;
      edge.right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      stuckToEnd = alongY()
        ? el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
        : el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
    }

    // Two of whatever the horizontal axis is made of: staff tracks in the
    // classic layout, turn slots when transposed.
    function nudge(dir) {
      var el = boardEl.value;
      if (!el) return;
      var probe = el.querySelector(alongY() ? '.cell' : '.col');
      var step = probe
        ? (probe.getBoundingClientRect().width + 8) * 2
        : el.clientWidth * 0.6;
      el.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    var arrowBack = Vue.computed(function () {
      return store.ui.boardLayout === 'rows' ? 'Earlier turns' : 'Earlier columns';
    });

    var arrowOn = Vue.computed(function () {
      return store.ui.boardLayout === 'rows' ? 'Later turns' : 'More columns';
    });

    // Keep somewhere the next turn could actually go on screen. The technicians
    // who are blank at the floor's earliest incomplete turn are exactly the
    // ones a turn can land in; if none of them is in view, bring the nearest
    // one in. Only runs when the board changes, never while scrolling, so it
    // cannot fight somebody looking around.
    //
    // Along the staff axis, which is the other one from everything above:
    // across the screen normally, down it when transposed.
    function ensureOpenTrackVisible() {
      var el = boardEl.value;
      if (!el) return;
      var track = el.querySelector('.cols');
      if (!track || !track.children.length) return;
      var staff = drawnStaff();
      var target = store.floorTurnsCompleted();
      var down = alongY();                       // staff run down the screen

      var gutter = el.querySelector('.gutter');
      var g = gutter ? gutter.getBoundingClientRect() : null;
      var pad = g ? (down ? g.height : g.width) + 8 : 0;
      var box = el.getBoundingClientRect();
      var boxStart = (down ? box.top : box.left) + pad;
      var boxEnd = down ? box.bottom : box.right;

      var best = null;
      for (var i = 0; i < staff.length && i < track.children.length; i++) {
        if (store.nextOpenRow(staff[i].id) !== target) continue;
        var r = track.children[i].getBoundingClientRect();
        var near = down ? r.top : r.left;
        var far = down ? r.bottom : r.right;
        if (near >= boxStart - 1 && far <= boxEnd + 1) return;   // one is visible
        var gap = near < boxStart ? boxStart - near : far - boxEnd;
        if (!best || gap < best.gap) best = { near: near, far: far, gap: gap };
      }
      if (!best) return;
      var delta = best.near < boxStart ? best.near - boxStart : best.far - boxEnd;
      el.scrollBy(down
        ? { top: delta, behavior: 'smooth' }
        : { left: delta, behavior: 'smooth' });
    }

    function refresh() {
      fitToWindow();
      Vue.nextTick(function () {
        if (stuckToEnd) scrollToEnd();
        onScroll();
        // A resize changes who fits, so the open track can fall out of view
        // without anybody scrolling.
        ensureOpenTrackVisible();
      });
    }

    /* A phone's toolbars collapsing is not a window resize — `innerHeight` does
     * not move, so nothing fires — but it is several rows' worth of board. The
     * visual viewport reports both, so it is listened to as well where there is
     * one, and `resize` alone everywhere else. */
    Vue.onMounted(function () {
      refresh();
      Vue.nextTick(ensureOpenTrackVisible);
      window.addEventListener('resize', refresh);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', refresh);
      }
    });
    Vue.onBeforeUnmount(function () {
      window.removeEventListener('resize', refresh);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', refresh);
      }
    });

    // Arming is the moment the eligible squares appear, and booking clears the
    // arm again — so this fires on the way in, not on the way out.
    Vue.watch(
      function () { return store.ui.armedServiceId; },
      function (id) {
        Vue.nextTick(function () {
          // On a phone arming raises a strip over the bottom of the window, so
          // the board is a strip shorter while something is armed — and a strip
          // taller again the moment it is put down.
          fitToWindow();
          if (id) revealEligible();
        });
      }
    );

    // Redraw on anything that changes the grid's shape.
    var lastTurnCount = store.state.turns.length;

    // The visible row count is in here as well as the total: hiding finished
    // rows changes the grid's height without changing the day's shape, and the
    // board still has to re-fit and settle its scroll after it.
    Vue.watch(
      function () {
        return store.previewColumns.value.length + ':' + store.rowCount.value +
          ':' + store.visibleRows.value.length + ':' + store.state.turns.length;
      },
      function () {
        var grew = store.state.turns.length > lastTurnCount;
        lastTurnCount = store.state.turns.length;
        Vue.nextTick(function () {
          fitToWindow();
          var newest = grew && newestTurn();
          // A new square wins over the resting position; without one, hold the
          // leading edge if that is where the user left it.
          if (!(newest && revealTurn(newest.id)) && stuckToEnd) scrollToEnd();
          onScroll();
          ensureOpenTrackVisible();
        });
      }
    );

    /* Turning the board round is a resize of every square on it, and of the box
     * they are in: what used to be the count of rows that fit is now a count of
     * columns, and the scroll position is measured on the other axis. Nothing
     * about the day changes, so this is only the geometry catching up — but it
     * has to catch up on this station when another one flips the setting, not
     * just when somebody presses the switch in front of them. */
    Vue.watch(
      function () { return store.ui.boardLayout; },
      function () {
        stuckToEnd = true;                 // the day's leading edge, either way
        Vue.nextTick(function () {
          fitToWindow();
          Vue.nextTick(function () {
            scrollToEnd();
            onScroll();
            ensureOpenTrackVisible();
          });
        });
      }
    );

    /* ---- the order lock ---- */

    var headTitle = Vue.computed(function () {
      return store.ui.lockOrder
        ? ' — right-click to skip, bring back or take off the board'
        : ' — drag to reorder, right-click to skip, bring back or take off the board';
    });

    // "The order", not "the column order": the same lock holds the same roster
    // whichever axis the names happen to be laid out along today.
    var lockTitle = Vue.computed(function () {
      return store.ui.lockOrder
        ? 'The board order is locked — click to unlock.'
        : 'The board order is unlocked — drag a name to move it. Click to lock it.';
    });

    /* ---- hiding finished rows ---- */

    var hideDoneTitle = Vue.computed(function () {
      var n = store.doneRowCount.value;
      if (!n) {
        return 'No turn is finished right across the floor yet — nothing to hide.';
      }
      return store.ui.hideDoneRows
        ? n + ' finished ' + (n === 1 ? 'turn is' : 'turns are') +
          ' hidden. The turn numbers keep counting, so the gaps are them.'
        : 'Fold away the ' + n + ' turn' + (n === 1 ? '' : 's') +
          ' the whole floor has finished, leaving what is still live.';
    });

    var hideDoneStaffTitle = Vue.computed(function () {
      var n = store.doneStaffCount.value;
      if (!n) return 'Nobody is done for today yet — nothing to hide.';
      var who = n === 1 ? 'technician who is' : 'technicians who are';
      return store.ui.hideDoneStaff
        ? 'The ' + n + ' ' + who + ' done for today ' + (n === 1 ? 'is' : 'are') +
          ' hidden. Somebody on a break stays on the board.'
        : 'Fold away the columns of the ' + n + ' ' + who + ' done for today. ' +
          'Somebody on a break stays on the board.';
    });

    /* ---- turning the board round ---- */

    /* The same setting as Settings → Board & day → Board layout, on the board
     * itself: the axes are the one thing you cannot judge from a menu, because
     * judging them means looking at the grid. store.setBoardLayout is the whole
     * of it — it saves for the salon and toasts, so a flip here and a flip there
     * are the same act, and the other stations follow either way.
     */
    var flipTo = Vue.computed(function () {
      return store.ui.boardLayout === 'rows' ? 'columns' : 'rows';
    });

    function flipLayout() { store.setBoardLayout(flipTo.value); }

    // The picture on the button is the board it would GIVE you, not the one
    // already on screen — the same twelve squares as the Settings picker, with
    // the staff headers along the top for a column each and down the side for a
    // row each. Positions are fixed; only which squares are headers changes.
    var miniSquares = (function () {
      var out = [];
      for (var r = 0; r < 3; r++) {
        for (var c = 0; c < 4; c++) {
          out.push({ key: r + ':' + c, row: r, col: c, x: 0.6 + c * 4, y: 2.4 + r * 4 });
        }
      }
      return out;
    })();

    function miniHead(sq) {
      return flipTo.value === 'rows' ? sq.col === 0 : sq.row === 0;
    }

    var flipTitle = Vue.computed(function () {
      return flipTo.value === 'rows'
        ? 'Turn the board round — a row for each technician, turns counting across.'
        : 'Turn the board round — a column for each technician, turns counting down.';
    });

    return {
      boardEl: boardEl,
      drag: drag,
      turnDrag: turnDrag,
      onTurnPointerDown: onTurnPointerDown,
      isGhost: isGhost,
      isLifted: isLifted,
      isSwapTarget: isSwapTarget,
      // Which turn numbers the grid draws. Every finished row drops out of
      // this when the switch is on, and the gutter and the columns both read
      // it, so they cannot disagree about what row a square is in.
      rows: store.visibleRows,
      doneRowCount: store.doneRowCount,
      canHideDoneRows: store.canHideDoneRows,
      toggleHideDoneRows: store.toggleHideDoneRows,
      hideDoneTitle: hideDoneTitle,
      doneStaffCount: store.doneStaffCount,
      toggleHideDoneStaff: store.toggleHideDoneStaff,
      hideDoneStaffTitle: hideDoneStaffTitle,
      flipLayout: flipLayout,
      flipTitle: flipTitle,
      miniSquares: miniSquares,
      miniHead: miniHead,
      rowStartTimes: store.rowStartTimes,
      formatClock: store.formatClock,
      ui: store.ui,
      // Every square's stable state, worked out once per change rather than
      // rebuilt by the template a few thousand times a second — minus any
      // rows the floor has finished, while those are being hidden, and with
      // the column under a drag redrawn as the drop would leave it.
      columns: store.previewColumns,
      presentStaff: store.presentStaff,
      suggestedStaffId: store.suggestedStaffId,
      isAttachTarget: isAttachTarget,
      isChooseTarget: isChooseTarget,
      isChooseLive: isChooseLive,
      mixTitle: mixTitle,
      isApptStartTarget: isApptStartTarget,
      isApptPairTarget: isApptPairTarget,
      isInsertTarget: isInsertTarget,
      isOver: isOver,
      isPairTarget: isPairTarget,
      pluralTurns: store.pluralTurns,
      cellState: cellState,
      isUnlinked: isUnlinked,
      unlinkedTitle: unlinkedTitle,
      mismatch: mismatch,
      mismatchTitle: mismatchTitle,
      turnTitle: turnTitle,
      onCellClick: onCellClick,
      onCellContext: onCellContext,
      onHeadContext: onHeadContext,
      onCellKey: onCellKey,
      onHeadKey: onHeadKey,
      cellTab: cellTab,
      cellLabel: cellLabel,
      skipBadge: skipBadge,
      skipTitle: skipTitle,
      shiftBadge: shiftBadge,
      shiftTitle: shiftTitle,
      optCount: store.optionCountByTurn,
      optTitle: optTitle,
      openAddStaff: store.openAddStaff,
      addable: store.addableStaff,
      toggleLockOrder: store.toggleLockOrder,
      headTitle: headTitle,
      lockTitle: lockTitle,
      edge: edge,
      onScroll: onScroll,
      nudge: nudge,
      arrowBack: arrowBack,
      arrowOn: arrowOn,
      onPointerDown: onPointerDown,
      elapsed: elapsed,
      timerText: timerText,
      fracTimerText: fracTimerText,
      turnDrift: driftText,
      turnPct: turnPct,
      fracLead: fracLead,
      fracDim: fracDim,
      timerState: timerState,
      timerTitle: timerTitle,
      offsetMark: offsetMark,
      turnColor: store.colorOfTurn,
      // The next clock reads the book. No plugin, no book — and the idle clock
      // takes the whole strip rather than sitting next to a permanent dash.
      showNext: Vue.computed(function () {
        return !!(store.schedulingOn.value && store.auth.user);
      }),
      idleLabel: idleLabel,
      idleTitle: idleTitle,
      nextLabel: nextLabel,
      nextTitle: nextTitle,
      nextClass: nextClass,
      nextChip: nextChip,
      peek: peek,
      peekDetail: peekDetail,
      openPeek: openPeek,
      holdPeek: holdPeek,
      closePeek: closePeek,
      peekWhen: peekWhen,
      peekLength: peekLength,
      peekLock: peekLock,
      peekCanMark: peekCanMark,
      peekStarTitle: peekStarTitle,
      togglePeekRequest: togglePeekRequest,
      coverLine: coverLine,
      coverNote: coverNote,
      peekStaffName: peekStaffName
    };
  },
  template: [
    '<div class="board-wrap">',
    '  <div v-if="!presentStaff.length" class="empty-state">',
    '    <h2>Nobody is on the board yet</h2>',
    '    <p><span>Add the technicians who are in today, or set the roster up under <strong>Settings &rarr; Staff</strong>.</span></p>',
    '    <button type="button" class="btn btn--primary" :disabled="!addable.length"',
    '      @click="openAddStaff">Add staff for today</button>',
    '  </div>',
    /* Above the grid rather than inside it: the board scrolls both ways, and a
       control pinned to a corner of a scroll box either travels with the
       squares or has to be stuck over them. */
    '  <div v-if="presentStaff.length" class="board-tools">',
    /* The Turns switch only once the day is long enough to have something to
       fold: it appears with the third turn. The axis button is there from the
       first square — which way round the board reads is a question you can
       have before anybody has finished anything. */
    /* Staff is there from the first square: anybody can be done for the day
       before the floor has finished a row. */
    '    <div class="board-tools__hide" role="group" aria-labelledby="board-tools-hide">',
    '      <span id="board-tools-hide" class="board-tools__hide-label">Hide Finished:</span>',
    '      <label v-if="canHideDoneRows" class="switch switch--sm board-tools__switch" :title="hideDoneTitle">',
    '        <input type="checkbox" :checked="ui.hideDoneRows" @change="toggleHideDoneRows" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Turns',
    '          <span v-if="doneRowCount" class="board-tools__n">{{ doneRowCount }}</span>',
    '        </span>',
    '      </label>',
    '      <label class="switch switch--sm board-tools__switch" :title="hideDoneStaffTitle">',
    '        <input type="checkbox" :checked="ui.hideDoneStaff" @change="toggleHideDoneStaff" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Staff',
    '          <span v-if="doneStaffCount" class="board-tools__n">{{ doneStaffCount }}</span>',
    '        </span>',
    '      </label>',
    '    </div>',
    '    <button type="button" class="board-tools__flip" @click="flipLayout"',
    '      :title="flipTitle" :aria-label="flipTitle">',
    '      <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">',
    '        <rect v-for="sq in miniSquares" :key="sq.key" :x="sq.x" :y="sq.y"',
    '          width="3.2" height="3.2" rx="0.8"',
    '          class="board-tools__sq" :class="{ \'is-head\': miniHead(sq) }" />',
    '      </svg>',
    '    </button>',
    '  </div>',
    '  <div v-if="presentStaff.length" class="board-scroll" :class="{ \'has-left\': edge.left, \'has-right\': edge.right }">',
    '    <button v-if="edge.left" type="button" class="board-arrow board-arrow--left"',
    '      @click="nudge(-1)" :title="arrowBack" :aria-label="arrowBack">&#8249;</button>',
    '    <button v-if="edge.right" type="button" class="board-arrow board-arrow--right"',
    '      @click="nudge(1)" :title="arrowOn" :aria-label="arrowOn">&#8250;</button>',
    /* Same markup either way round — see the axis note at the top of this file.
       The class is the whole of the transposition: the stylesheet turns the
       flex directions and the sticky edges over, and the geometry above reads
       the same setting it comes from. */
    '  <div class="board" ref="boardEl" @scroll="onScroll"',
    '    :class="{ \'board--locked\': ui.lockOrder, \'board--rows\': ui.boardLayout === \'rows\',',
    /* The pointer says what the release will do. On the whole board rather than
       on the square under it: the drag holds a pointer capture, so the cursor
       is taken from the square that was picked UP, which is somewhere else
       entirely by the time the answer is yes. */
    '                \'board--swapping\': turnDrag.kind === \'swap\' }">',
    '    <div class="gutter">',
    '      <div class="gutter__head">Turn</div>',
    '      <div v-for="row in rows" :key="row" class="gutter__row">',
    '        <span class="gutter__num">{{ row + 1 }}</span>',
    '        <span v-if="rowStartTimes[row]" class="gutter__time"',
    '          :title="\'Turn \' + (row + 1) + \' started at \' + formatClock(rowStartTimes[row])">',
    '          {{ formatClock(rowStartTimes[row]) }}</span>',
    '      </div>',
    '    </div>',
    '    <transition-group name="col" tag="div" class="cols">',
    '      <div v-for="(col, pos) in columns" :key="col.staff.id" class="col"',
    '        :class="{ \'col--dragging\': drag.active && drag.staffId === col.staff.id,',
    /* Which column a square in the air would land in, and whether it would.
       On the column rather than on a square, because the answer is about the
       whole of it: the square it would land on is already showing the ghost. */
    '                  \'col--taking\': turnDrag.overStaffId === col.staff.id && turnDrag.kind === \'move\',',
    '                  \'col--refusing\': turnDrag.overStaffId === col.staff.id && turnDrag.kind === \'blocked\',',
    '                  \'col--skipped\': col.skipped,',
    '                  \'col--off-shift\': col.offShift && !col.skipped }">',
    '        <div class="col__head" @pointerdown="onPointerDown($event, col.staff, pos)"',
    '          @contextmenu="onHeadContext($event, col.staff)"',
    '          tabindex="0" aria-haspopup="menu" @keydown="onHeadKey($event, col.staff)"',
    '          :title="col.staff.name + headTitle">',
    '          <span v-if="!ui.lockOrder" class="col__grip" aria-hidden="true"></span>',
    '          <span class="col__name">{{ col.staff.name }}</span>',
    '          <span class="col__meta">',
    '            <span class="col__turns">{{ pluralTurns(col.credit) }}</span>',
    '            <span v-if="col.skipped" class="col__badge col__badge--skip"',
    '              :title="skipTitle(col)">{{ skipBadge(col) }}</span>',
    /* One badge only — a narrow column cannot hold two without truncating the
       turn count. Exact queue order stays visible in the Up next strip.

       Ahead of Busy, unlike every other badge here, because a last square
       still running on somebody who is leaving says less than the time they
       are leaving at: the square has its own countdown on it already, and
       what the desk needs off the header is that nothing follows it. */
    '            <span v-else-if="col.offShift" class="col__badge col__badge--gone"',
    '              :title="shiftTitle(col)">{{ shiftBadge(col) }}</span>',
    /* Ahead of Busy for the same reason as the hours: the running square says
       they are working, and what the header has to say is that nothing from
       the rotation follows it. */
    '            <span v-else-if="col.ownClients" class="col__badge col__badge--own"',
    '              :title="col.staff.name + \' takes only clients who ask for them — out of the rotation\'">Own</span>',
    '            <span v-else-if="col.busy" class="col__badge col__badge--busy">Busy</span>',
    '            <span v-else-if="col.openHalf" class="col__badge col__badge--half"',
    '              title="Owed the other half of a half turn — next for any service">&frac12;</span>',
    '          </span>',
    /* Without a book to read — no plugin, or no session — "next" would read as
       an empty diary rather than as unknown, so the idle clock goes it alone. */
    '          <span class="col__clock">',
    /* Nothing stands in for the idle clock while they are working: the square's
       own running timer already says they are in service. */
    '            <span v-if="!col.busy" class="col__clock-item" :title="idleTitle(col)">',
    '              <span class="col__clock-key">idle</span>{{ idleLabel(col) }}</span>',
    /* The letter and the countdown are one hover target: they are one fact
       about one booking, and a card that opened from half of it would be a
       card that closed as the pointer crossed to the other half. */
    '            <span v-if="showNext" class="col__clock-item col__clock-item--next"',
    '              :class="[nextClass(col), { \'is-open\': peek.staffId === col.staff.id }]"',
    '              :title="nextTitle(col)" tabindex="0"',
    '              @pointerenter="openPeek($event, col)" @pointerleave="closePeek"',
    '              @focus="openPeek($event, col)" @blur="closePeek">',
    '              <span v-if="nextChip(col)" class="col__next-svc"',
    '                :style="{ \'--chip\': nextChip(col).color }">{{ nextChip(col).letter }}',
    '                <span v-if="nextChip(col).requested" class="col__next-star"',
    '                  aria-label="Client request">&#9733;</span>',
    '              </span>',
    '              <span class="col__clock-key">next</span>{{ nextLabel(col) }}</span>',
    '          </span>',
    '        </div>',
    /* `cell` carries everything about the square that does not move with the
       pointer, precomputed in store.boardColumns. The handful of calls left
       are the drag and arm states, and each of those returns immediately when
       nothing is armed and nothing is in the air. */
    '        <div v-for="cell in col.cells" :key="cell.row" class="cell"',
    '          :class="[\'cell--\' + cellState(col, cell), {',
    '            \'cell--suggested\': suggestedStaffId === col.staff.id && cell.row === col.targetRow,',
    '            \'cell--lifted\': isLifted(cell),',
    '            \'cell--ghost\': isGhost(cell),',
    '            \'cell--swap-ok\': isSwapTarget(col, cell),',
    '            \'cell--swap-live\': isSwapTarget(col, cell) && turnDrag.swapTurnId === cell.runningTurnId,',
    '            \'cell--pair\': isPairTarget(col, cell),',
    '            \'cell--pair-ok\': isApptPairTarget(col, cell),',
    '            \'cell--pair-live\': isOver(\'pair\', col, cell),',
    '            \'cell--attach-ok\': isAttachTarget(col, cell),',
    '            \'cell--attach-live\': isOver(\'attach\', col, cell),',
    '            \'cell--choose-ok\': isChooseTarget(col, cell),',
    '            \'cell--choose-live\': isOver(\'choose\', col, cell),',
    '            \'cell--start-ok\': isApptStartTarget(col, cell),',
    '            \'cell--start-live\': isOver(\'start\', col, cell),',
    '            \'cell--insert-ok\': isInsertTarget(col, cell),',
    '            \'cell--insert-live\': isOver(\'insert\', col, cell),',
    '            \'cell--mismatch\': cell.mismatch,',
    '            \'cell--zero\': cell.zero,',
    '            \'cell--frac\': cell.half,',
    /* A half square borrowing the whole square's strip: the two halves make
       room for it. See `fracLead`. */
    '            \'cell--frac-bar\': !!fracLead(cell),',
    '            \'cell--frac-open\': cell.open,',
    '            \'cell--labelled\': cell.labelled }]"',
    /* The service colour, for whatever wants it beyond the strip: the running
       square's outline reads `--svc`, and fell back to gold without this. Not
       on a half square holding two services: its strip is a two-colour
       gradient, which is not a colour. */
    '          :style="cell.strip && cell.strip.indexOf(\'gradient\') < 0 ? { \'--svc\': cell.strip } : null"',
    '          :data-turn-id="cell.runningTurnId"',
    '          :data-staff-id="col.staff.id" :data-row="cell.row"',
    '          @pointerdown="onTurnPointerDown($event, col, cell)"',
    '          @contextmenu="onCellContext($event, col, cell)"',
    '          @click="onCellClick($event, col, cell)"',
    '          role="button" :tabindex="cellTab(col, cell)" :aria-label="cellLabel(col, cell)"',
    '          @keydown="onCellKey($event, col, cell)">',
    '',
    /* Over an occupied square there is no room to write in, so the offer is
       said over the top of what is already there rather than instead of it. */
    '          <span v-if="isSwapTarget(col, cell)" class="cell__swap">',
    '            <span class="cell__swap-mark" aria-hidden="true">&#8646;</span>swap</span>',
    '',
    /* ---- half turns: a fraction sharing one square ---- */
    '          <template v-if="cell.half">',
    '            <span class="cell__strip" :style="{ background: cell.strip }"></span>',
    '            <span class="frac__slash"></span>',
    '            <span v-for="(t, i) in cell.turns" :key="t.id"',
    '              class="frac__part"',
    '              :class="[i === 0 ? \'frac__part--a\' : \'frac__part--b\',',
    '                       { \'frac__part--dim\': fracDim(cell, t),',
    /* Which of the two the card would land on. The square's own outline says
       it will take the drop; this says into which half, which on a shared
       square is a different client's history. */
    '                         \'frac__part--choose\': isChooseLive(t) }]"',
    '              :title="turnTitle(t)">',
    /* Who it is, above the letter, exactly as a whole square carries it. The
       name was the one thing a half dropped, and it is the thing the desk
       reads a square for — a client asking which chair she is in should not
       have to be looked up in the sheet because her manicure happened to
       share a square. Truncated rather than wrapped: half a square is narrow,
       and a first name usually fits. */
    '              <span v-if="t.clientName" class="frac__client">{{ t.clientName }}</span>',
    /* Half a square has no room for a service name, so the letter stands in
       for it and the + goes beside that instead. */
    '              <span class="frac__letter" :style="{ color: turnColor(t) }">{{ t.serviceLetter }}',
    '                <span v-if="optCount[t.id]" class="frac__opt"',
    '                  :title="optTitle(t)" :aria-label="optTitle(t)">+</span>',
    '                <span v-if="ui.requestsOn && t.requested" class="frac__star" title="Client request">&#9733;</span>',
    '                <span v-if="isUnlinked(t)" class="frac__unlinked" :title="unlinkedTitle"></span>',
    '                <span v-else-if="mismatch(t)" class="frac__warn"',
    '                  :title="mismatchTitle(t)">&#9888;</span></span>',
    /* The same mark half a square gets. It is the size a half square can spare
       and it is why the indicator is colour: there is no room here for a
       second letter and there never was. */
    '              <span v-if="cell.mixes[i]" class="cell__mix frac__mix"',
    '                :title="mixTitle(t)" :aria-label="mixTitle(t)">',
    '                <span v-for="(c, j) in cell.mixes[i]" :key="j" class="cell__mix-chip"',
    '                  :style="{ background: c }"></span>',
    '              </span>',
    /* Both pills go while the strip is carrying the time. The running half's
       would be the same countdown twice on one square, in a place too narrow
       to say it properly; the finished half's would fit at the sizes a wide
       screen gives a square and not at the narrowest, which is the size the
       board has to be right at. What it said — how long that half took and how
       far off its booked length that was — is on the square's tooltip and in
       the sheet a tap opens, in words rather than in five characters. */
    '              <span v-if="!fracLead(cell)" class="frac__timer"',
    '                :class="[timerState(t), { \'is-running\': t.endedAt == null,',
    '                                          \'has-drift\': turnDrift(t) }]"',
    '                :title="timerTitle(t)">{{ fracTimerText(t) }}</span>',
    '            </span>',
    '            <span v-if="cell.turns.length < 2" class="frac__part frac__part--b frac__open"',
    '              title="Half turn still open">&nbsp;</span>',
    /* The whole square's strip, on a square that is only half of one — same
       markup, same states, same fill as a whole turn's, and the reason the two
       halves above have moved up out of the bottom of the square. */
    '            <span v-if="fracLead(cell)" class="cell__timer"',
    '              :class="[timerState(fracLead(cell)), { \'is-running\': true,',
    '                       \'cell__timer--bar\': turnPct(fracLead(cell)) != null }]"',
    '              :title="timerTitle(fracLead(cell))">',
    '              <span v-if="offsetMark(fracLead(cell))" class="cell__timer-offset"',
    '                :class="\'is-\' + offsetMark(fracLead(cell)).kind"',
    '                :style="{ width: offsetMark(fracLead(cell)).pct + \'%\' }" aria-hidden="true"></span>',
    '              <span v-if="turnPct(fracLead(cell)) != null" class="cell__timer-fill"',
    '                :style="{ width: turnPct(fracLead(cell)) + \'%\' }" aria-hidden="true"></span>',
    '              <span class="cell__timer-text">{{ timerText(fracLead(cell)) }}</span>',
    '            </span>',
    '          </template>',
    '',
    /* ---- a whole turn ---- */
    '          <template v-else-if="cell.first">',
    '            <span class="cell__strip" :style="{ background: cell.strip }"></span>',
    '            <span v-if="ui.requestsOn && cell.first.requested" class="cell__req" title="Client request">&#9733;</span>',
    '            <span v-if="isUnlinked(cell.first)" class="cell__unlinked"',
    '              :title="unlinkedTitle" aria-label="No appointment attached"></span>',
    '            <span v-else-if="mismatch(cell.first)" class="cell__warn"',
    '              :title="mismatchTitle(cell.first)"',
    '              aria-label="Wrong technician on the booking">&#9888;</span>',
    '            <span v-if="cell.first.clientName" class="cell__label">',
    '              <span class="cell__client">{{ cell.first.clientName }}</span>',
    /* The + says the booking carries options without trying to name them: a
       square is too small to read a list off, and the names are one tap away
       in the sheet. Its own line-box so a long service name ellipsises
       without taking the mark with it. */
    '              <span class="cell__svc-line">',
    '                <span class="cell__svc">{{ cell.first.sourceServiceName }}</span>',
    '                <span v-if="optCount[cell.first.id]" class="cell__opt-mark"',
    '                  :title="optTitle(cell.first)"',
    '                  :aria-label="optTitle(cell.first)">+</span>',
    '              </span>',
    '            </span>',
    '            <span class="cell__letter"',
    '              :class="{ \'cell__letter--sm\': cell.first.clientName || cell.zero }">',
    '              {{ cell.first.serviceLetter }}</span>',
    /* More than one service on this one square. The letter above is still the
       first of them — a merged square is a manicure that also had a pedicure,
       not a new kind of thing — and this says there is more, in the colours the
       board is already read by. What the rest are is a tap away. */
    '            <span v-if="cell.mixes[0]" class="cell__mix" :title="mixTitle(cell.first)"',
    '              :aria-label="mixTitle(cell.first)">',
    '              <span v-for="(c, i) in cell.mixes[0]" :key="i" class="cell__mix-chip"',
    '                :style="{ background: c }"></span>',
    '            </span>',
    /* The strip is the same strip either way the salon draws a live square —
       same place, same colours, same wording. With the bar on it fills behind
       the countdown instead of blinking beside it; with no length to measure
       against there is nothing to fill, and the light stays. See `turnPct`. */
    '            <span class="cell__timer"',
    '              :class="[timerState(cell.first), { \'is-running\': cell.first.endedAt == null,',
    '                                                 \'cell__timer--bar\': turnPct(cell.first) != null }]"',
    '              :title="timerTitle(cell.first)">',
    '              <span v-if="offsetMark(cell.first)" class="cell__timer-offset"',
    '                :class="\'is-\' + offsetMark(cell.first).kind"',
    '                :style="{ width: offsetMark(cell.first).pct + \'%\' }" aria-hidden="true"></span>',
    '              <span v-if="turnPct(cell.first) != null" class="cell__timer-fill"',
    '                :style="{ width: turnPct(cell.first) + \'%\' }" aria-hidden="true"></span>',
    '              <span class="cell__timer-text">{{ timerText(cell.first) }}</span>',
    '            </span>',
    '          </template>',
    '',
    '          <template v-else-if="cellState(col, cell) === \'blocked\'">',
    '            <span class="cell__lock">in service</span>',
    '          </template>',
    '        </div>',
    '      </div>',
    '    </transition-group>',
    /* Outside the transition-group on purpose: it is not a column and must
       never be a drag target or take part in the reorder animation. */
    '    <div class="add-col">',
    '      <button type="button" class="add-col__btn" @click="openAddStaff"',
    '        :disabled="!addable.length"',
    '        :title="addable.length ? \'Add someone else in for today\' : \'Everyone on the roster is already in\'"',
    '        aria-label="Add staff in for today">+</button>',
    '      <button type="button" class="add-col__btn add-col__btn--lock"',
    '        :class="{ \'is-on\': ui.lockOrder }" @click="toggleLockOrder"',
    '        :title="lockTitle" :aria-pressed="ui.lockOrder ? \'true\' : \'false\'"',
    '        aria-label="Lock the board order">',
    '        <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">',
    '          <path v-if="ui.lockOrder" d="M5 7V4.5a3 3 0 0 1 6 0V7"',
    '            fill="none" stroke="currentColor" stroke-width="1.5" />',
    '          <path v-else d="M8 7V4.5a3 3 0 0 1 6 0V5.8"',
    '            fill="none" stroke="currentColor" stroke-width="1.5" />',
    '          <rect x="3" y="7" width="10" height="7" rx="1.6" fill="currentColor" />',
    '        </svg>',
    '      </button>',
    '    </div>',
    '  </div>',
    '  </div>',
    /* Outside the scroll box, fixed to the window: inside it the card would be
       clipped by the very overflow that lets the board scroll. */
    '  <div v-if="peekDetail" class="peek-card next-peek"',
    '    :style="{ left: peek.x + \'px\', top: peek.y + \'px\', maxHeight: peek.maxH + \'px\' }"',
    '    @pointerenter="holdPeek" @pointerleave="closePeek">',
    /* Above the time and hard right, where nothing else on the card sits, so
       it is found the same way every time and never shifts as the card's
       wording changes underneath it. */
    '    <div v-if="peekCanMark || (ui.requestsOn && peekDetail.requested)" class="next-peek__mark">',
    '      <button v-if="peekCanMark" type="button" class="star-btn" :class="{ \'is-on\': peekDetail.requested }"',
    '        :title="peekStarTitle" :aria-label="peekStarTitle"',
    '        :aria-pressed="peekDetail.requested ? \'true\' : \'false\'"',
    '        @click="togglePeekRequest">{{ peekDetail.requested ? "\\u2605" : "\\u2606" }}</button>',
    '      <span v-else class="star-btn is-on is-booked" title="A client request in the book"',
    '        aria-label="A client request in the book">&#9733;</span>',
    '    </div>',
    '    <div class="peek-card__head">{{ peekWhen(peekDetail) }}</div>',
    '    <div class="next-peek__svc">',
    '      <span class="next-peek__letter"',
    '        :style="{ \'--chip\': peekDetail.mapped ? peekDetail.mapped.color : \'#8a93a6\' }">',
    '        {{ peekDetail.mapped ? peekDetail.mapped.abbrev : "?" }}</span>',
    /* No star beside the name any more: the one at the top of the card is
       gold when this is a request, and two stars saying one thing on a card
       this size reads as two different facts. */
    '      <span class="next-peek__name">{{ peekDetail.svc.name }}</span>',
    '    </div>',
    '    <div class="next-peek__client">{{ peekDetail.appt.clientName }}<client-note :client-id="peekDetail.appt.clientId" />',
    '      <span v-if="peekLength(peekDetail)" class="next-peek__len">{{ peekLength(peekDetail) }}</span>',
    '    </div>',
    /* What the booking really is, when an option has changed it. The letter
       above is already the changed one, so without this the card shows a Gel
       Pedicure for something the book plainly calls a Classic. */
    '    <div v-if="peekDetail.options.length" class="next-peek__opts">',
    '      <span v-for="o in peekDetail.options" :key="o.id" class="next-peek__opt">{{ o.name }}</span>',
    '    </div>',
    '    <div v-if="!peekDetail.mapped" class="next-peek__unmapped">',
    '      Not mapped to a turn type yet &mdash; Settings &rarr; Mangomint.',
    '    </div>',
    '    <div v-if="peekLock(peekDetail, peekStaffName)" class="peek-card__note"',
    '      :class="{ \'is-req\': peekDetail.requested }">',
    '      {{ peekLock(peekDetail, peekStaffName) }}',
    '    </div>',
    /* Only when it is not a request. A request is a promise to one person, so
       there is nobody else to list — that is the whole meaning of the star. */
    '    <template v-if="!peekDetail.requested && peekDetail.mapped">',
    '      <div v-if="coverLine(peekDetail)" class="next-peek__cover-line">{{ coverLine(peekDetail) }}</div>',
    '      <ul v-if="peekDetail.cover.length" class="next-peek__cover">',
    '        <li v-for="c in peekDetail.cover" :key="c.staff.id" :class="{ \'is-free\': c.ok }">',
    '          <span class="next-peek__who">{{ c.staff.name }}<note-warn :client-id="peekDetail.appt.clientId" :staff-id="c.staff.id" /></span>',
    '          <span class="next-peek__why">{{ coverNote(c) }}</span>',
    '        </li>',
    '      </ul>',
    '    </template>',
    '  </div>',
    '</div>'
  ].join('\n')
};
